/* Desktop-only disclosures; native links and tab order remain unchanged. */
(() => {
  if (customElements.get('rymar-desktop-dropdown')) return;

  class RymarDesktopDropdown extends HTMLElement {
    connectedCallback() {
      this.details = this.querySelector('details');
      this.summary = this.details?.querySelector(':scope > summary');
      this.panel = this.querySelector('.rymar-desktop-dropdown__panel');
      if (!this.details || !this.summary || !this.panel) return;

      this.listeners?.abort();
      this.listeners = new AbortController();
      const signal = this.listeners.signal;
      this.desktop = matchMedia('(min-width: 1000px)');
      this.finePointer = matchMedia('(hover: hover) and (pointer: fine)');
      this.navigation = this.closest('.header__primary-nav');
      this.header = this.closest('x-header') || this.closest('header');

      this.summary.addEventListener('click', (event) => {
        if (!this.desktop.matches) return;
        event.preventDefault();
        this.details.open ? this.close() : this.open();
      }, { signal });

      this.addEventListener('pointerenter', (event) => {
        this.cancelClose();
        this.cancelOpen();
        if (!this.usesHover(event)) return;
        const siblingOpen = Array.from(this.navigation?.querySelectorAll('rymar-desktop-dropdown') || [])
          .some((sibling) => sibling !== this && sibling.details?.open);
        // Crossing another trigger on the way into a wide panel must not
        // immediately replace that panel. Deliberate hover still opens promptly.
        if (siblingOpen) this.openTimer = setTimeout(() => this.open(), 120);
        else this.open();
      }, { signal });
      this.addEventListener('pointerleave', (event) => {
        this.cancelOpen();
        if (!this.usesHover(event)) return;
        this.cancelClose();
        this.closeTimer = setTimeout(() => {
          // Pointer movement must not dismiss a menu being used with the keyboard.
          if (!this.contains(document.activeElement)) this.close();
        }, 180);
      }, { signal });

      this.addEventListener('focusin', () => this.cancelClose(), { signal });
      this.addEventListener('focusout', (event) => {
        if (event.relatedTarget) {
          if (!this.contains(event.relatedTarget)) this.close();
          return;
        }
        clearTimeout(this.focusTimer);
        this.focusTimer = setTimeout(() => {
          if (this.isConnected && !this.contains(document.activeElement)) this.close();
        }, 0);
      }, { signal });
      document.addEventListener('pointerdown', (event) => {
        if (!this.contains(event.target)) this.close();
      }, { signal });
      document.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape' || !this.details.open) return;
        const restoreFocus = this.contains(document.activeElement);
        this.close();
        if (restoreFocus) {
          event.preventDefault();
          event.stopPropagation();
          this.summary.focus({ preventScroll: true });
        }
      }, { signal });

      this.details.addEventListener('toggle', () => {
        if (this.details.open && !this.desktop.matches) this.close();
        this.summary.setAttribute('aria-expanded', String(this.details.open));
        if (this.details.open) this.schedulePosition();
      }, { signal });

      // Native toggle does not bubble; capture also observes the unchanged
      // Prestige disclosures, so opening a fallback menu dismisses this panel.
      this.navigation?.addEventListener('toggle', (event) => {
        if (event.target instanceof HTMLDetailsElement && event.target.open &&
            event.target.getAttribute('aria-expanded') !== 'false' && !this.contains(event.target)) {
          this.close();
        }
      }, { capture: true, signal });

      this.desktop.addEventListener('change', () => {
        if (!this.desktop.matches) this.close();
        else this.schedulePosition();
      }, { signal });
      window.addEventListener('resize', () => this.schedulePosition(), { passive: true, signal });
      document.addEventListener('scroll', () => this.schedulePosition(), { capture: true, passive: true, signal });
      window.visualViewport?.addEventListener('resize', () => this.schedulePosition(), { passive: true, signal });

      if (this.header && typeof ResizeObserver !== 'undefined') {
        this.resizeObserver = new ResizeObserver(() => this.schedulePosition());
        this.resizeObserver.observe(this.header);
      }

      this.addEventListener('shopify:block:select', () => this.open(), { signal });
      this.addEventListener('shopify:block:deselect', () => this.close(), { signal });
      this.summary.setAttribute('aria-expanded', String(this.details.open));
      if (!this.desktop.matches) this.close();
      else if (this.details.open) this.open();
    }

    disconnectedCallback() {
      this.listeners?.abort();
      this.resizeObserver?.disconnect();
      this.cancelClose();
      this.cancelOpen();
      clearTimeout(this.focusTimer);
      cancelAnimationFrame(this.positionFrame);
      this.positionFrame = null;
    }

    usesHover(event) {
      return this.desktop.matches && this.getAttribute('trigger') === 'hover' &&
        this.finePointer.matches && event.pointerType !== 'touch';
    }

    cancelClose() {
      clearTimeout(this.closeTimer);
    }

    cancelOpen() {
      clearTimeout(this.openTimer);
    }

    open() {
      if (!this.desktop.matches) return;
      this.cancelClose();
      this.cancelOpen();

      this.navigation?.querySelectorAll('rymar-desktop-dropdown').forEach((sibling) => {
        if (sibling !== this) sibling.close();
      });
      this.navigation?.querySelectorAll('dropdown-menu-disclosure, mega-menu-disclosure').forEach((sibling) => {
        if (sibling.querySelector(':scope > details[open]')) sibling.close?.();
      });

      this.details.open = true;
      this.summary.setAttribute('aria-expanded', 'true');
      this.position();
    }

    close() {
      this.cancelClose();
      this.cancelOpen();
      this.details.open = false;
      this.summary.setAttribute('aria-expanded', 'false');
    }

    schedulePosition() {
      if (!this.details.open || this.positionFrame) return;
      this.positionFrame = requestAnimationFrame(() => {
        this.positionFrame = null;
        if (this.isConnected && this.details.open) this.position();
      });
    }

    position() {
      if (!this.desktop.matches || !this.details.open) return;
      const host = this.getBoundingClientRect();
      const headerBottom = this.header?.getBoundingClientRect().bottom || host.bottom;
      const top = Math.max(headerBottom, host.bottom);
      const viewportWidth = document.documentElement.clientWidth;
      const viewportHeight = window.visualViewport?.height || window.innerHeight;
      const gutter = 16;
      const width = Math.min(this.panel.getBoundingClientRect().width, viewportWidth - gutter * 2);
      const left = Math.max(gutter, Math.min(host.left, viewportWidth - gutter - width));

      this.style.setProperty('--rymar-nav-panel-x', `${left - host.left}px`);
      this.style.setProperty('--rymar-nav-panel-y', `${top - host.top}px`);
      this.style.setProperty('--rymar-nav-panel-width', `${width}px`);
      this.style.setProperty('--rymar-nav-panel-max-height', `${Math.max(0, viewportHeight - top - gutter)}px`);
      this.style.setProperty('--rymar-nav-bridge-x', `${left - host.left}px`);
      this.style.setProperty('--rymar-nav-bridge-width', `${width}px`);
      this.style.setProperty('--rymar-nav-bridge-height', `${Math.max(0, top - host.bottom)}px`);
    }
  }

  customElements.define('rymar-desktop-dropdown', RymarDesktopDropdown);
})();
